import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, Container, Stack, TextField } from '@mui/material';

import Page from '@/components/Page';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import { SkeletonLoadingTableData } from '@/components/skeleton';

import { ROOTS_SETTINGS } from '../routes/paths';
import { getEmailTemplates } from '../api';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { EmailTemplateEditor, EmailTemplateStyleEditor } from './template-form';
import { EmailTemplateStyleFormValues } from '../types';

export default function EmailTemplates() {
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [previewStyle, setPreviewStyle] =
    useState<EmailTemplateStyleFormValues | null>(null);
  const { errorMsg } = useSnackbarMsg();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: getEmailTemplates,
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching email templates'),
  });

  useEffect(() => {
    if (!selectedTemplateId && templates.length) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [selectedTemplateId, templates]);

  const handleTemplateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTemplateId(Number(event.target.value));
  };

  return (
    <Page title="Settings: Email Templates">
      <Container maxWidth={false}>
        <HeaderBreadcrumbs
          heading="Email Templates"
          links={[
            { name: 'Settings', href: ROOTS_SETTINGS },
            { name: 'Email Templates' },
          ]}
        />

        <Stack spacing={3}>
          <EmailTemplateStyleEditor onStyleChange={setPreviewStyle} />

          <Card>
            <CardContent>
              <TextField
                fullWidth
                select
                SelectProps={{ native: true }}
                size="small"
                label="Template"
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                disabled={isLoading || !templates.length}
              >
                <option value="" disabled>
                  {isLoading ? 'Loading templates...' : 'Select a template'}
                </option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </TextField>
            </CardContent>
          </Card>

          {isLoading ? (
            <SkeletonLoadingTableData />
          ) : selectedTemplateId ? (
            <EmailTemplateEditor
              templateId={Number(selectedTemplateId)}
              previewStyle={previewStyle}
            />
          ) : (
            <Card>
              <CardContent>No email templates found.</CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </Page>
  );
}
